/**
 * DimensionInputField — floating numeric input(s) shown on the 2nd and
 * subsequent draft points when Dimension Input is active (DYNMODE bit 2).
 *
 * AutoCAD parity:
 *  - Single-field kinds (length / radius / diameter / rotation / angle):
 *    one numeric field, shown for DYNDIVIS≥1.
 *  - Two-field kinds (width-height, distance-angle): both fields ALWAYS
 *    rendered regardless of DYNDIVIS — these commands are intrinsically
 *    2D and AutoCAD never shows just one field for them.
 *      • width-height — RECTANGLE / MTEXT second corner, array spacing.
 *      • distance-angle — LINE / PLINE / ARC-end continuation.
 *  - Keyboard: Tab / Shift+Tab cycle between primary↔secondary (when two
 *    fields are visible), Enter commits, Esc cancels. Tab locks the field
 *    being left so the user can refine the other.
 *  - The component shares the same `FieldState` reducer as PointerInputFields
 *    (driven by DynamicInputOverlay) — no separate state machine needed.
 *
 * Layout: positioned at `screenPosition` (CSS pixels relative to the stage
 * shell), matching the cursor-following overlay pattern from Phase 1.
 */

import * as React from "react";
import { useEffect, useRef } from "react";

import { useT } from "../i18n";
import type { ScreenPoint } from "./fieldGeometry";
import type { FieldEvent, FieldKey, FieldState } from "./fieldBehavior";
import {
  detectCoordMode,
  splitCoordBuffer,
  type CoordMode,
} from "./fieldBehavior";
import type { DimFieldSpec } from "./draftStepFieldConfig";

// ─── Props ────────────────────────────────────────────────────────────────────

export type DimensionInputFieldProps = {
  /** When false the component renders nothing (kind="none" or dim disabled). */
  visible: boolean;
  /** CSS-pixel position of the fields container relative to the stage shell. */
  screenPosition: ScreenPoint;
  /** Which kind of dimension field to render (from draftStepFieldConfig). */
  spec: DimFieldSpec;
  /** Current field state (shared with PointerInputFields via DynamicInputOverlay). */
  state: FieldState;
  /**
   * Live cursor-derived values displayed as placeholders when the user
   * has not typed into the corresponding field.
   */
  liveValues: { primary: string; secondary: string };
  /**
   * Hint that DYNDIVIS=2 (two-field mode) is enabled. Two-field kinds
   * (width-height, distance-angle) ALWAYS render both fields regardless
   * of this flag — this prop is kept for backwards compatibility and
   * for any future DYNDIVIS-gated behavior on single-field kinds.
   */
  twoFields: boolean;
  /** Dispatch a field event upward to the reducer in DynamicInputOverlay. */
  onEvent: (event: FieldEvent) => void;
  /** Called when Enter is pressed — parent builds the dim token and commits. */
  onSubmit: () => void;
  /** Called when Esc is pressed — parent cancels the draft or clears state. */
  onEscape: () => void;
};

// ─── Component ────────────────────────────────────────────────────────────────

export function DimensionInputField(
  props: DimensionInputFieldProps,
): React.ReactElement | null {
  const t = useT();
  const primaryRef = useRef<HTMLInputElement>(null);
  const secondaryRef = useRef<HTMLInputElement>(null);

  const {
    visible,
    spec,
    state,
    liveValues,
    onEvent,
    onSubmit,
    onEscape,
  } = props;

  // Auto-focus the correct input whenever focused changes or visibility toggles.
  useEffect(() => {
    if (!visible || !state.typing) return;
    if (state.focused === "primary") {
      primaryRef.current?.focus();
    } else if (state.focused === "secondary") {
      secondaryRef.current?.focus();
    }
  }, [visible, state.typing, state.focused]);

  if (!visible || spec.kind === "none") return null;

  // ── Key handler (shared between both inputs) ────────────────────────────

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    _key: FieldKey,
  ) {
    if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) {
        onEvent({ type: "shift-tab" });
      } else {
        onEvent({ type: "tab" });
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onEscape();
      return;
    }
  }

  // ── Field value / placeholder helpers ──────────────────────────────────

  function getFieldValue(key: FieldKey): string {
    if (state.locks[key] !== null) {
      return String(state.locks[key]);
    }
    if (state.typing) {
      return state.buffer[key];
    }
    return "";
  }

  function getPlaceholder(key: FieldKey): string {
    if (state.locks[key] !== null) return "";
    return liveValues[key];
  }

  function isReadOnly(key: FieldKey): boolean {
    return state.locks[key] !== null;
  }

  // ── Labels & coord-mode detection ─────────────────────────────────────

  const baseLabel = t(spec.primaryLabelKey as Parameters<typeof t>[0]);
  // AutoCAD parity: the moment the user's buffer parses as a coordinate
  // expression (@dx,dy | #x,y | @d<a | plain x,y) the field visually
  // splits into two editable inputs (X & Y, or dist & angle) — matching
  // AutoCAD's Pointer Input UI, where Dimension Input turns into two
  // numeric fields as soon as a separator appears.
  const coordMode: CoordMode = detectCoordMode(state.buffer.primary);
  const isCoordMode = coordMode !== "none";

  // Labels for coord-mode primary & secondary (e.g. "@ΔX" / "ΔY").
  const coordPrimaryLabel =
    coordMode === "relative-cartesian"
      ? t("dynin.field.coord_prefix_rel_cart")
      : coordMode === "absolute-cartesian"
        ? t("dynin.field.coord_prefix_abs_cart")
        : coordMode === "relative-polar"
          ? t("dynin.field.coord_prefix_rel_polar")
          : coordMode === "absolute-polar"
            ? t("dynin.field.coord_prefix_abs_polar")
            : "";
  const coordSecondaryLabel =
    coordMode === "relative-cartesian"
      ? t("dynin.field.coord_sec_rel_cart")
      : coordMode === "absolute-cartesian"
        ? t("dynin.field.coord_sec_abs_cart")
        : coordMode === "relative-polar" || coordMode === "absolute-polar"
          ? t("dynin.field.coord_sec_angle")
          : "";

  // Two-field DI kinds: render both inputs regardless of DYNDIVIS (matches
  // AutoCAD behavior — these commands are intrinsically 2D, never shown as
  // a single field).
  const isTwoFieldKind =
    spec.kind === "width-height" || spec.kind === "distance-angle";

  const primaryLabel = isCoordMode ? coordPrimaryLabel : baseLabel;
  const secondaryLabel = isCoordMode
    ? coordSecondaryLabel
    : isTwoFieldKind && spec.secondaryLabelKey
      ? t(spec.secondaryLabelKey as Parameters<typeof t>[0])
      : "";

  // Split buffer for two-field render when in coord mode.
  const coordSplit = isCoordMode
    ? splitCoordBuffer(state.buffer.primary, coordMode)
    : null;

  // Show two visible fields when either coord-mode is active or the spec
  // kind is intrinsically two-field (width-height / distance-angle).
  const renderSecondary = isCoordMode || isTwoFieldKind;

  // Separator between primary and secondary fields:
  //  - coord-mode: derived from buffer (`,` or `<`)
  //  - distance-angle: `<` (polar style)
  //  - width-height (and any future cartesian kind): `,`
  const dimSeparator: "," | "<" =
    spec.kind === "distance-angle" ? "<" : ",";

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      className="dynin-fields-container dynin-dim-fields"
      style={{
        left: props.screenPosition.leftPx,
        top: props.screenPosition.topPx,
      }}
      data-testid="dynin-dim-fields"
      data-coord-mode={coordMode}
    >
      {/* Primary field label */}
      <span className="dynin-field-label">{primaryLabel}</span>

      {/* Primary input — in coord mode, value = left part of split buffer */}
      <input
        ref={primaryRef}
        type="text"
        inputMode="decimal"
        className="dynin-field"
        data-testid="dynin-dim-field-primary"
        data-focused={state.focused === "primary" ? "true" : "false"}
        data-locked={isReadOnly("primary") ? "true" : "false"}
        data-invalid={state.invalid.primary ? "true" : "false"}
        readOnly={isReadOnly("primary")}
        value={coordSplit ? coordSplit.left : getFieldValue("primary")}
        placeholder={getPlaceholder("primary")}
        aria-label={primaryLabel}
        onKeyDown={(e) => handleKeyDown(e, "primary")}
        onChange={(e) => {
          if (isReadOnly("primary")) return;
          const v = e.target.value;
          if (coordSplit) {
            // AutoCAD parity: if the user typed the separator (`,` or `<`)
            // into the primary, auto-strip it and hop focus to secondary.
            if (v.endsWith(",") || v.endsWith("<")) {
              const stripped = v.slice(0, -1);
              onEvent({
                type: "change-buffer",
                key: "primary",
                value: stripped + coordSplit.sep + coordSplit.right,
              });
              onEvent({ type: "tab" });
              return;
            }
            // Reconstruct full buffer by replacing only the left part.
            onEvent({
              type: "change-buffer",
              key: "primary",
              value: v + coordSplit.sep + coordSplit.right,
            });
          } else {
            onEvent({ type: "change-buffer", key: "primary", value: v });
          }
        }}
        onFocus={() => {
          // Focus is managed via the reducer; no separate event dispatched.
        }}
      />

      {/* Secondary — shown in coord mode OR for any two-field DI kind. */}
      {renderSecondary && (
        <>
          {/* Separator (, or <) between primary and secondary. */}
          <span className="dynin-separator" aria-hidden="true">
            {coordSplit ? coordSplit.sep : dimSeparator}
          </span>

          {/* Secondary field label */}
          <span className="dynin-field-label">{secondaryLabel}</span>

          {/* Secondary input */}
          <input
            ref={secondaryRef}
            type="text"
            inputMode="decimal"
            className="dynin-field"
            data-testid="dynin-dim-field-secondary"
            data-focused={state.focused === "secondary" ? "true" : "false"}
            data-locked={isReadOnly("secondary") ? "true" : "false"}
            data-invalid={
              coordSplit ? "false" : state.invalid.secondary ? "true" : "false"
            }
            readOnly={isReadOnly("secondary")}
            value={coordSplit ? coordSplit.right : getFieldValue("secondary")}
            placeholder={
              coordSplit ? liveValues.secondary : getPlaceholder("secondary")
            }
            aria-label={secondaryLabel}
            onKeyDown={(e) => handleKeyDown(e, "secondary")}
            onChange={(e) => {
              if (isReadOnly("secondary")) return;
              const v = e.target.value;
              if (coordSplit) {
                // Reconstruct full buffer on primary by replacing right part.
                onEvent({
                  type: "change-buffer",
                  key: "primary",
                  value: coordSplit.left + coordSplit.sep + v,
                });
              } else {
                onEvent({ type: "change-buffer", key: "secondary", value: v });
              }
            }}
            onFocus={() => {
              // Focus managed by reducer.
            }}
          />
        </>
      )}
    </div>
  );
}
